import { DoubleLeftOutlined, DoubleRightOutlined } from "@ant-design/icons";
import { Button, Layout, Menu, Row, Space, Tooltip, Typography } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { selectIsDevEnvironment } from "@tailormed/common-client/store/selector";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "@tailormed/common-client/services/auth";
import { SwitchingBetweenApps } from "@tailormed/common-client/component/SwitchingBetweenApps";

import {
  BLACK,
  DARK_BLUE,
  GRAY_700,
  GRAY_900,
  PRIMARY_100,
  PRIMARY_25,
  PRIMARY_400,
  PRIMARY_50,
  WHITE
} from "../constant/colors";
import { TailormedLogoIcon } from "./AppLayout";
import { ReactComponent as FinancialInsightsLogo } from "../assets/svg/financial-insights-icon.svg";
import { ReactComponent as SignOutIcon } from "../assets/svg/signout-icon.svg";
import { ReactComponent as ContactSupportIcon } from "../assets/svg/contact-support-icon.svg";
import { fontWeights, sizes } from "../constant/styles";
import { history } from "../router/history";
import { selectIDPConfig, selectMenuItems, selectUser } from "../store/selector";
import { oktaConfig } from "../constant/okta";
import ACTIONS from "../store/action";
import { Routes } from "../constant/routes";
import { MENU_ICONS } from "../constant/menu";
import { ReactComponent as ArrowDownIcon } from "../assets/svg/arrow-down-icon.svg";
import { useGetSetting } from "../hooks/getSetting";

const { Sider } = Layout;
const { Text } = Typography;

const SITE_NAME = "insights";

const TailormedLogoSpace = styled(Space)`
  display: flex;
  justify-content: flex-start;
  cursor: pointer;
`;

const FinancialInsightsSpace = styled(Space)`
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.collapsed ? "center" : "start")};
  cursor: pointer;
  position: relative;
  padding: 6px;
  margin-right: ${(props) => (props.collapsed ? "0px" : "84px")};
  margin-top: ${(props) => (props.collapsed ? "40px" : "0px")};
  right: ${({ collapsed, hasModulesToSwitch }) => (collapsed ? (hasModulesToSwitch ? "12px" : "8px") : "0px")};
  width: ${({ collapsed, hasModulesToSwitch }) => (collapsed ? (hasModulesToSwitch ? "55px" : "42px") : undefined)};
  height: ${(props) => (props.collapsed ? "32px" : undefined)};

  &:hover {
    background: ${PRIMARY_25};
    border-radius: 8px;
  }

  &:active {
    background: ${PRIMARY_50};
    border-radius: 8px;
  }
`;

const FinancialInsightsText = styled(Text)`
  font-size: ${sizes.medium};
  font-weight: ${fontWeights.semibold};
  color: ${GRAY_700};
  display: flex;
`;

const ToggleButton = styled(Button)`
  font-size: ${sizes.medium_large};
  background: ${GRAY_700};
  width: 30px;
  height: 30px;
  position: absolute;
  background: ${PRIMARY_50};
  border-radius: 50%;
  ${(props) => {
    if (props.collapsed) {
      return `left: 65px; `;
    }
  }}

  ${(props) => {
    if (!props.collapsed) {
      return `left: 285px; `;
    }
  }}

   ${(props) => {
    if (props.isDevEnvironment) {
      return ` top: 45px; `;
    }
  }}

  ${(props) => {
    if (!props.isDevEnvironment) {
      return ` top: 20px; `;
    }
  }}
   &:hover {
    background: ${PRIMARY_100} !important;
  }

  &:disabled {
    opacity: 0.3;
  }

  :focus {
    background: ${PRIMARY_50};
  }

  :active {
    border: 3px solid ${PRIMARY_400} !important;
    background: ${PRIMARY_50} !important;
  }
`;

const FooterWrapper = styled.div`
  position: absolute;
  bottom: 0px;
  width: 100%;
`;

const TitleGroupStyle = styled(Row)`
  font-size: ${sizes.medium};
  font-weight: ${fontWeights.semibold};
  background: ${PRIMARY_50};
  display: flex;
  ${(props) => {
    if (!props.collapsed) {
      return `justify-content: start;
              padding: 8px 0px 8px 20px; `;
    }
  }}
  ${(props) => {
    if (props.collapsed) {
      return `justify-content: center;
              padding: 8px 0px;`;
    }
  }}
  margin: 0px 15px;
  border-radius: 8px;
  .ant-typography {
    color: ${DARK_BLUE};
    margin-left: 8px;
    justify-content: center;
    display: flex;
    align-items: center;
  }
`;

const StyledSider = styled(Sider)`
  background: ${WHITE};
  border: 0px;

  display: flex;
  flex-direction: column;
  height: calc(100vh - 30px);

  .ant-layout-sider-children {
    display: flex;
    flex-direction: column;
  }

  .ant-menu-inline {
    border: 0px;
  }

  .ant-layout-sider-children .ant-menu.ant-menu-inline-collapsed {
    border: 0px;
  }

  ul > li {
    margin-top: 11px;
  }
  .ant-menu-vertical .ant-menu-item::after,
  .ant-menu-vertical-left .ant-menu-item::after,
  .ant-menu-vertical-right .ant-menu-item::after,
  .ant-menu-inline .ant-menu-item::after {
    border: 0px;
  }

  .ant-menu-light .ant-menu-item:hover,
  .ant-menu-light .ant-menu:not(.ant-menu-inline) .ant-menu-submenu-open,
  .ant-menu-light .ant-menu-submenu-active,
  .ant-menu-light .ant-menu-submenu-title:hover {
    background-color: transparent;
    border-radius: 8px;
    width: auto;
    margin-left: 0;

    span {
      display: flex;
      background-color: ${PRIMARY_25};
      margin-left: 8px;
      margin-right: 16px;
      border-radius: 8px;
      align-items: center;
      height: 28px;
    }

    span > span {
      color: ${BLACK};
      margin-left: 45px !important;
    }
  }

  .ant-menu-inline.ant-menu-root .ant-menu-item > .ant-menu-title-content,
  .ant-menu-inline.ant-menu-root .ant-menu-submenu-title > .ant-menu-title-content {
    span {
      color: ${GRAY_900};
      font-size: ${sizes.medium};
      font-weight: ${fontWeights.regular};
      margin-left: 53px;
    }
  }

  .ant-menu-inline.ant-menu-root .ant-menu-item {
    margin: 0px;
  }

  .ant-menu:not(.ant-menu-horizontal) .ant-menu-item-selected {
    background-color: transparent;
    border-radius: 8px;
    width: auto;
    margin-left: 0;

    span {
      display: flex;
      background-color: ${PRIMARY_25};
      margin-left: 8px;
      margin-right: 16px;
      border-radius: 8px;
      align-items: center;
      height: 28px;
    }

    span > span {
      color: ${BLACK};
      margin-left: 45px !important;
    }
  }

  .ant-menu-item:active,
  .ant-menu-submenu-title:active {
    background: transparent;
  }

  .ant-menu ul,
  .ant-menu ol {
    ${(props) => {
      if (props.collapsed) {
        return `display: none;`;
      }
    }}
  }

  .ant-menu-item-group-title {
    ${(props) => {
      if (!props.collapsed) {
        return ` padding: 0px 16px;
        `;
      }
    }}
  }

  .ant-menu-inline.ant-menu-root .ant-menu-item {
    margin: 1px 0px 0px 0px;
    height: 22px;
  }

  ul > li > ul > li:first-child {
    margin-top: 4px !important;
  }

  #footer {
    font-weight: ${fontWeights.regular};
    font-size: 11px;
    color: ${BLACK};
    display: flex;
    margin: 50px 0px 0px 35px;
  }
`;

const MenuTitle = styled(Row)`
  font-size: ${sizes.medium};
  font-weight: ${fontWeights.semibold};
  background: ${PRIMARY_50};
  display: flex;
  align-items: center;

  height: 34px;
  ${(props) => {
    if (!props.collapsed) {
      return `justify-content: start;
              padding: 0px 0px 0px 20px; `;
    }
  }}
  ${(props) => {
    if (props.collapsed) {
      return `justify-content: center;
              padding: 8px 0px;`;
    }
  }}
  margin: 0px 15px;
  border-radius: 8px;
  .ant-typography {
    color: ${GRAY_900};
    margin-left: 8px;
    justify-content: center;
    display: flex;
    align-items: center;
  }
`;

const LogoContainer = styled(Space)`
  padding: 25px 0px 0px 30px;
  gap: 3px !important;
  width: 100%;
`;

const SideBar = () => {
  const { t } = useTranslation();
  const { location } = history;
  const { pathname } = location;
  const dispatch = useDispatch();

  const isDevEnvironment = useSelector(selectIsDevEnvironment);
  const idpConfig = useSelector(selectIDPConfig);
  const user = useSelector(selectUser);

  const [collapsed, setCollapsed] = useState(false);
  const [selectedSubMenuKey, setSelectedSubMenuKey] = useState([]);

  const sideBarMenus = useSelector(selectMenuItems) || [];

  useEffect(() => {
    const pathNames = pathname.split("/").filter((i) => i && i !== SITE_NAME);
    if (pathNames.length) {
      const key = pathNames.slice(-1)[0];
      setSelectedSubMenuKey(key);
    } else {
      setSelectedSubMenuKey([]);
    }
  }, [location]);

  const handleSignOut = useCallback(() => {
    logout(user, idpConfig, oktaConfig.loginURI);
  }, []);

  const handleContactSupport = () => {
    dispatch(ACTIONS.setShowContactSupportModal(true));
  };

  const goToHomePage = () => {
    history.push({
      pathname: Routes.ROOT,
      state: undefined
    });
  };

  return (
    <>
      <StyledSider
        collapsible
        collapsed={collapsed}
        onCollapse={(value) => setCollapsed(value)}
        trigger={null}
        width="300px"
      >
        <LogoContainer direction="vertical">
          <TailormedLogoSpace id="tailormed_logo" onClick={goToHomePage}>
            {!collapsed && <TailormedLogoIcon />}
          </TailormedLogoSpace>

          <SwitchingBetweenApps
            user={user}
            goToHomePage={goToHomePage}
            useGetSetting={useGetSetting}
            pathname={pathname}
          >
            {({ hasModulesToSwitch }) => (
              <FinancialInsightsSpace
                id="financial_insights"
                collapsed={collapsed}
                hasModulesToSwitch={hasModulesToSwitch}
                onClick={() => {
                  if (!hasModulesToSwitch) {
                    goToHomePage();
                  }
                }}
              >
                <FinancialInsightsLogo />
                {!collapsed && (
                  <div>
                    <FinancialInsightsText>{t("financial_insights")}</FinancialInsightsText>
                  </div>
                )}
                {hasModulesToSwitch && <ArrowDownIcon style={{ marginBottom: collapsed ? "5px" : "0px" }} />}
              </FinancialInsightsSpace>
            )}
          </SwitchingBetweenApps>
        </LogoContainer>

        <Menu mode="inline" style={{ marginTop: "20px" }} selectedKeys={selectedSubMenuKey}>
          {sideBarMenus.MenuItems?.length
            ? sideBarMenus.MenuItems?.map((menu) => (
                <Menu.ItemGroup
                  key={menu.key}
                  id={menu.key}
                  title={
                    <Tooltip title={menu.title} placement="right" overlayStyle={{ display: !collapsed && "none" }}>
                      <MenuTitle
                        collapsed={collapsed}
                        id={menu.key}
                        onClick={() => {
                          if (menu?.subMenus[0]) {
                            history.push({
                              pathname: Routes.ROOT + Routes.SELECT + menu.subMenus[0].title,
                              state: { url: menu.subMenus[0].url }
                            });
                          }
                        }}
                        style={{ cursor: "pointer" }}
                      >
                        {MENU_ICONS[menu.key]}
                        {!collapsed && <Text>{menu.title}</Text>}
                      </MenuTitle>
                    </Tooltip>
                  }
                >
                  {menu?.subMenus?.map((subMenu, index) => (
                    <Menu.Item
                      icon={subMenu.icon}
                      onClick={() => {
                        history.push({
                          pathname: Routes.ROOT + Routes.SELECT + subMenu.title,
                          state: { url: subMenu.url, title: subMenu.title }
                        });
                      }}
                      key={subMenu.title}
                      id={`${menu.key}_${index}_sub_menu`}
                    >
                      {!collapsed && <Text>{subMenu.title}</Text>}
                    </Menu.Item>
                  ))}
                </Menu.ItemGroup>
              ))
            : []}
        </Menu>

        <FooterWrapper>
          <Menu mode="inline">
            <Menu.ItemGroup
              style={{ cursor: "pointer" }}
              title={
                <Tooltip
                  title={t("sidebar_menu.sign_out")}
                  placement="right"
                  overlayStyle={{ display: !collapsed && "none" }}
                >
                  <TitleGroupStyle collapsed={collapsed} onClick={handleSignOut} id="sign_out_btn">
                    {<SignOutIcon />}
                    {!collapsed && <Text>{t("sidebar_menu.sign_out")}</Text>}
                  </TitleGroupStyle>
                </Tooltip>
              }
            />
            <Menu.ItemGroup
              style={{ cursor: "pointer" }}
              title={
                <Tooltip
                  title={t("sidebar_menu.contact_support")}
                  placement="right"
                  overlayStyle={{ display: !collapsed && "none" }}
                >
                  <TitleGroupStyle collapsed={collapsed} onClick={handleContactSupport} id="contact_support_btn">
                    {<ContactSupportIcon />}
                    {!collapsed && <Text>{t("sidebar_menu.contact_support")}</Text>}
                  </TitleGroupStyle>
                </Tooltip>
              }
            />
          </Menu>
          {!collapsed && <Text id="footer">{t("footer", { year: new Date().getFullYear() })}</Text>}
        </FooterWrapper>
      </StyledSider>

      <ToggleButton
        type="text"
        icon={
          collapsed ? (
            <DoubleRightOutlined style={{ color: DARK_BLUE }} />
          ) : (
            <DoubleLeftOutlined style={{ color: DARK_BLUE }} />
          )
        }
        onClick={() => setCollapsed(!collapsed)}
        collapsed={collapsed}
        isDevEnvironment={isDevEnvironment}
      />
    </>
  );
};

export { SideBar };
